// /src/components/SSBProgramConcentrationReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import SSBReportShell from "./SSBReportShell";
import SSBFiltersBar from "./SSBFiltersBar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";


const API_BASE =
  process.env.REACT_APP_SSB_API_BASE ||
  (window.location.hostname === "localhost"
    ? "http://localhost:9191"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app");
const money0 = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(Number(n));

const pct2 = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v > 0 && v < 0.005) return "< 0.01%";
  return `${v.toFixed(2)}%`;
};

const shortMonth = (m) => (m || "").slice(0, 10);

const Pill = ({ text, tone = "neutral" }) => {
  const map = {
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good: { bg: "#ECFDF3", fg: "#027A48" },
    warn: { bg: "#FFFAEB", fg: "#B54708" },
    bad: { bg: "#FEF3F2", fg: "#B42318" },
    info: { bg: "#EFF8FF", fg: "#175CD3" },
  };
  const c = map[tone] || map.neutral;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 800,
        border: "1px solid rgba(0,0,0,0.04)",
      }}
    >
      {text}
    </span>
  );
};

function TooltipBox({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div
      style={{
        background: "white",
        border: "1px solid #E7ECF3",
        borderRadius: 12,
        padding: 10,
        boxShadow: "0 16px 40px rgba(16,24,40,0.12)",
        maxWidth: 360,
      }}
    >
      <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 6 }}>{p.name}</div>
      <div style={{ fontSize: 13, color: "#0B1F33" }}>
        <div>Total Volume: <b>{money0(p.total_amount)}</b></div>
        <div>Portfolio Share: <b>{pct2(p.pct_of_portfolio)}</b></div>
      </div>
    </div>
  );
}

// Group months into period options
function buildPeriodMeta(months, periodType) {
  if (periodType === "month") {
    return months.map((m) => ({ label: shortMonth(m), months: [m] }));
  }
  const map = new Map();
  for (const m of months) {
    const year = m.slice(0, 4);
    const q = Math.ceil(parseInt(m.slice(5, 7)) / 3);
    const key = `${year}-Q${q}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  return Array.from(map.entries()).map(([label, ms]) => ({ label, months: ms }));
}

// Aggregate rows across multiple months, keyed on whichever name field is present
function aggregateRows(results, nameKey) {
  const pgMap = new Map();
  for (const r of results) {
    for (const row of r.rows || []) {
      const key = row[nameKey];
      if (!key) continue;
      const cur = pgMap.get(key) || { [nameKey]: key, total_amount: 0 };
      cur.total_amount += Number(row.total_amount || 0);
      pgMap.set(key, cur);
    }
  }
  const total = Array.from(pgMap.values()).reduce((s, r) => s + r.total_amount, 0);
  const rows = Array.from(pgMap.values())
    .sort((a, b) => b.total_amount - a.total_amount)
    .map((r) => ({ ...r, pct_of_portfolio: total > 0 ? (r.total_amount / total) * 100 : 0 }));
  return { rows };
}

const PERIOD_TYPES = ["month", "quarter"];

const VIEW_CONFIG = {
  program: {
    label: "Program",
    filterEndpoint: "program-concentration",
    dataEndpoint: "programs/concentration",
    nameKey: "program_name",
    chartTitle: "Portfolio Share by Program — % of total volume",
    tableHeader: "Program",
    footerSource: "mv_ssb_reports_program_monthly_mix",
  },
  product: {
    label: "Product",
    filterEndpoint: "product-concentration",
    dataEndpoint: "products/concentration",
    nameKey: "product_name",
    chartTitle: "Portfolio Share by Product — % of total volume",
    tableHeader: "Product",
    footerSource: "mv_ssb_reports_product_monthly_mix",
  },
};

export default function SSBProgramConcentrationReport() {
  const [months, setMonths] = useState([]);
  const [periodType, setPeriodType] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [viewType, setViewType] = useState("program");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const periodMeta = useMemo(() => buildPeriodMeta(months, periodType), [months, periodType]);

  const cfg = VIEW_CONFIG[viewType];

  // Load months filter
  useEffect(() => {
    let mounted = true;
    (async () => {
      setErr("");
      try {
        const resp = await fetch(`${API_BASE}/v1/ssb/filters/${cfg.filterEndpoint}`);
        const json = await resp.json();
        const m = Array.isArray(json.months) ? json.months : [];
        if (!mounted) return;
        setMonths(m);
      } catch (e) {
        console.error(e);
        if (mounted) setErr("Failed to load months. Is the API running?");
      }
    })();
    return () => { mounted = false; };
  }, [cfg.filterEndpoint]);

  // Initialize selectedPeriod when periodMeta changes
  useEffect(() => {
    if (periodMeta.length > 0) {
      setSelectedPeriod((prev) => {
        const stillValid = periodMeta.some((m) => m.label === prev);
        return stillValid ? prev : periodMeta[0].label;
      });
    }
  }, [periodMeta]);

  // Load report
  useEffect(() => {
    if (!selectedPeriod || periodMeta.length === 0) return;

    const meta = periodMeta.find((m) => m.label === selectedPeriod);
    if (!meta) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const results = await Promise.all(
          meta.months.map(async (monthStart) => {
            const url = new URL(`${API_BASE}/v1/ssb/${cfg.dataEndpoint}`);
            url.searchParams.set("month_start", monthStart);
            const resp = await fetch(url.toString());
            if (!resp.ok) return null;
            return resp.json();
          })
        );

        if (!mounted) return;

        const valid = results.filter(Boolean);
        const combined = valid.length === 1 ? valid[0] : aggregateRows(valid, cfg.nameKey);
        setRows(Array.isArray(combined.rows) ? combined.rows : []);
      } catch (e) {
        console.error(e);
        if (mounted) setErr(`Failed to load report: ${e.message || e}`);
        if (mounted) setRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [selectedPeriod, periodMeta, cfg.dataEndpoint, cfg.nameKey]);

  const summary = useMemo(() => {
    const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    const top3 = rows.slice(0, 3).reduce((s, r) => s + Number(r.total_amount || 0), 0);
    const top5 = rows.slice(0, 5).reduce((s, r) => s + Number(r.total_amount || 0), 0);
    const top3Pct = total > 0 ? (top3 / total) * 100 : 0;
    const top5Pct = total > 0 ? (top5 / total) * 100 : 0;
    return { total, top3Pct, top5Pct };
  }, [rows]);

  const bannerTone = summary.top3Pct >= 80 ? "bad" : summary.top3Pct >= 65 ? "warn" : "good";

  const chartData = useMemo(() => {
    return rows
      .filter((r) => r[cfg.nameKey])
      .map((r) => ({
        name: r[cfg.nameKey],
        pct_of_portfolio: Number(r.pct_of_portfolio || 0),
        total_amount: Number(r.total_amount || 0),
      }));
  }, [rows, cfg.nameKey]);

  const periodLabel = periodType === "quarter" ? "Quarter" : "Month";

  const toggleStyle = (active) => ({
    padding: "7px 18px",
    borderRadius: 8,
    border: `1.5px solid ${active ? "#3730A3" : "#D7DEE9"}`,
    background: active ? "#EEF2FF" : "#fff",
    color: active ? "#3730A3" : "#64748B",
    fontSize: 14,
    fontWeight: active ? 800 : 500,
    cursor: "pointer",
    lineHeight: 1.5,
  });

  return (
    <SSBReportShell
      title={`SSB Dashboard — ${cfg.label} Concentration`}
      subtitle={`${periodLabel}: ${selectedPeriod || "—"}`}
      rightMeta={["Portfolio Oversight", loading ? "Loading…" : "Live"]}
      leftMeta={["Sponsor bank oversight view", `Concentration by ${cfg.label.toLowerCase()}`]}
      footerLeft="Idenza • SSB Reporting (Phase 1)"
      footerRight={`Source: ${cfg.footerSource}`}
    >
      {/* Filters */}
      <SSBFiltersBar>
        {/* View toggle — Program vs Product */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>View</div>
          <div style={{ display: "flex", gap: 6 }}>
            {Object.keys(VIEW_CONFIG).map((v) => (
              <button key={v} onClick={() => setViewType(v)} style={toggleStyle(viewType === v)}>
                {VIEW_CONFIG[v].label}
              </button>
            ))}
          </div>
        </div>

        {/* Period selector */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>Period</div>
            <div style={{ display: "flex", gap: 3 }}>
              {PERIOD_TYPES.map((t) => (
                <button key={t} onClick={() => setPeriodType(t)} style={toggleStyle(periodType === t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            {periodMeta.map((m) => (
              <option key={m.label} value={m.label}>{m.label}</option>
            ))}
          </select>
        </div>

        <div />
        <div />
      </SSBFiltersBar>

      {/* Banner */}
      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E7ECF3",
          borderRadius: 16,
          padding: 14,
          boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Pill text="Concentration Signal" tone={bannerTone} />
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0B1F33" }}>
              Top 3 {cfg.label.toLowerCase()}s represent {summary.top3Pct.toFixed(1)}% of total {periodLabel.toLowerCase()} volume
            </div>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>
            Total portfolio volume: <b>{money0(summary.total)}</b> • Top 5 share: <b>{summary.top5Pct.toFixed(1)}%</b>
          </div>
        </div>

        <div>{loading ? <Pill text="Loading…" tone="neutral" /> : <Pill text="Live" tone="good" />}</div>
      </div>

      {/* Error */}
      {err ? (
        <div
          style={{
            background: "#FEF3F2",
            border: "1px solid #FECACA",
            color: "#7F1D1D",
            padding: 12,
            borderRadius: 16,
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      ) : null}

      {/* Chart + Table side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 12 }}>
        <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
          <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 8 }}>
            {cfg.chartTitle}
          </div>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            <div style={{ height: Math.max(380, chartData.length * 36) }}>
              {loading ? (
                <div style={{ padding: 12, color: "#64748B" }}>Loading…</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `${Number(v).toFixed(1)}%`} style={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={180} style={{ fontSize: 11 }} />
                    <Tooltip content={<TooltipBox />} />
                    <Bar dataKey="pct_of_portfolio" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
          <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 8 }}>Details</div>
          <div style={{ maxHeight: 500, overflowY: "auto", overflowX: "auto", border: "1px solid #F1F5F9", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #E7ECF3", background: "#F8FAFC", position: "sticky", top: 0, zIndex: 1 }}>
                  <th style={{ padding: 10 }}>#</th>
                  <th style={{ padding: 10 }}>{cfg.label}</th>
                  <th style={{ padding: 10 }}>Total Volume</th>
                  <th style={{ padding: 10 }}>Portfolio Share</th>
                </tr>
              </thead>
              <tbody>
                {rows.filter((r) => r[cfg.nameKey]).map((r, idx) => {
                  const share = Number(r.pct_of_portfolio || 0);
                  const tone = share >= 40 ? "bad" : share >= 25 ? "warn" : "neutral";
                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: 10, color: "#94A3B8", fontWeight: 700 }}>{idx + 1}</td>
                      <td style={{ padding: 10, fontWeight: 900, color: "#0B1F33" }}>{r[cfg.nameKey]}</td>
                      <td style={{ padding: 10 }}>{money0(r.total_amount)}</td>
                      <td style={{ padding: 10 }}><Pill text={pct2(r.pct_of_portfolio)} tone={tone} /></td>
                    </tr>
                  );
                })}
                {!loading && rows.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: 12, color: "#64748B" }}>No data for selected period.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SSBReportShell>
  );
}